<script lang="ts">
  import { mdiFullscreen } from "@mdi/js"
  import type { Snippet } from "svelte"
  import CardWrapper from "../../../layout/_modules/card-wrapper/index"
  import Icon from "../../../primitives/_modules/icon/index"
  import SectionTitle from "../../../typography/_modules/section-title/index"
  import AssistWidgetMount from "../assist-widget-mount/index"
  import AssistWidgetMenuButton from "../assist-widget-menu-button/index"
  import type { AssistWidgetConfig } from "../../../../core/assist/index"
  import { isMobile } from "../../../../core/overlay/globalStores"
  import type { WidgetLoaderOptions } from "../../../../core/assist/index"

  type LoaderOverrides = Partial<WidgetLoaderOptions>

  type Props = {
    config?: AssistWidgetConfig | null
    title?: string | null
    showMenu?: boolean | null
    showFullscreenShortcut?: boolean | null
    /** @deprecated Use showMenu instead. */
    showHistory?: boolean
    /** @deprecated History is available through the widget menu. */
    onOpenHistory?: (() => void) | null
    header?: Snippet | null
    rightCorner?: Snippet | null
    element?: "web-chat-frame" | "web-chat"
    elementAttributes?: Record<string, string | null | undefined> | null
    loaderOptions?: LoaderOverrides | null
    height?: string | null
    class?: string
    style?: string
    cardStyle?: string
  }

  let {
    config = null,
    title = "Assistenz",
    showMenu = null,
    showFullscreenShortcut = false,
    showHistory = true,
    header: headerContent = null,
    rightCorner: rightCornerContent = null,
    element = "web-chat-frame",
    elementAttributes = null,
    loaderOptions = null,
    height = null,
    class: className = "",
    style = "",
    cardStyle = "",
  }: Props = $props()

  let widgetElement = $state<HTMLElement | null>(null)
  const resolvedShowMenu = $derived(showMenu ?? showHistory)
  const fullscreenShortcutVisible = $derived(Boolean(showFullscreenShortcut && $isMobile))
  const menuVisible = $derived(Boolean(resolvedShowMenu && !fullscreenShortcutVisible))
  const chatWrapperStyle = $derived(height ? `--chat-frame-height: ${height};` : "")

  type FullscreenCapableElement = HTMLElement & {
    openFullscreen?: () => void
  }

  const handleOpenFullscreen = () => {
    const target = widgetElement as FullscreenCapableElement | null

    if (typeof target?.openFullscreen !== "function") return

    target.openFullscreen()
  }
</script>

<div class={`embedded-chat-frame ${className}`.trim()} {style}>
  <CardWrapper style="{cardStyle} height: 100%">
    {#snippet header()}
      {#if headerContent}
        {@render headerContent()}
      {:else if title}
        <SectionTitle {title} />
      {/if}
    {/snippet}
    {#snippet rightCorner()}
      {#if rightCornerContent}
        {@render rightCornerContent()}
      {:else if menuVisible || fullscreenShortcutVisible}
        <div class="embedded-chat-frame__actions">
          {#if fullscreenShortcutVisible}
            <button
              class="btn embedded-chat-frame__fullscreen-button"
              type="button"
              onclick={handleOpenFullscreen}
              title="Vollbild öffnen"
              aria-label="Vollbild öffnen"
              disabled={!widgetElement}
            >
              <Icon path={mdiFullscreen} />
            </button>
          {/if}
          {#if menuVisible}
            <AssistWidgetMenuButton {widgetElement} />
          {/if}
        </div>
      {/if}
    {/snippet}
    {#snippet children()}
      <div class="chat-wrapper" style={chatWrapperStyle}>
        <AssistWidgetMount
          {config}
          renderEmbed={true}
          {element}
          {elementAttributes}
          {loaderOptions}
          bind:widgetElement
        />
      </div>
    {/snippet}
  </CardWrapper>
</div>

<style lang="less">
  .embedded-chat-frame {
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .chat-wrapper {
    width: 100%;
    height: var(--chat-frame-height, 100%);
    min-height: 0;
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .embedded-chat-frame__actions {
    display: inline-flex;
    align-items: center;
    gap: 0.1rem;
  }

  .embedded-chat-frame__fullscreen-button {
    cursor: pointer;
  }

  .embedded-chat-frame__fullscreen-button:disabled {
    cursor: default;
    opacity: 0.45;
  }
</style>
